import api from '../client';

interface RefreshTokenResponse {
  access: string;
  refresh: string;
}

export interface Clinic {
  id: number;
  name: string;
  abn: string;
  address: string;
  specialty: string;
}

interface UserData {
  id: string;
  email: string;
  title?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  dob?: string;
  user_role: string;
  communication_preference?: string;
  verified?: boolean;
  clinic?: Clinic | null;
}

export interface LoginResponse {
  user: UserData;
  access: string;
  redirect_uri: string;
  refresh: string;
}

export interface ActivateUserPayload {
  uid: string;
  token: string;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface PasswordResetResponse {
  message: string;
}

export interface ChangePasswordError {
  current_password?: string[];
  new_password?: string[];
  confirm_password?: string[];
  non_field_errors?: string[];
}

export interface ProfileUpdatePayload {
  title?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  dob?: string;
  communication_preference?: string;
}

export interface ClinicUpdatePayload {
  name?: string;
  abn?: string;
  address?: string;
  specialty?: string;
}

export interface ProfileClinicUpdatePayload {
  user_profile: ProfileUpdatePayload;
  clinic_details: ClinicUpdatePayload;
}

export interface ProfileUpdateResponse {
  message: string;
  user: UserData;
}

export interface ClinicUpdateResponse {
  message: string;
  clinic: {
    id: number;
    name: string;
    abn: string;
    address: string;
    specialty: string;
  };
}

export interface ProfileClinicUpdateResponse {
  message: string;
  user: UserData;
  clinic: {
    id: number;
    name: string;
    abn: string;
    address: string;
    specialty: string;
  };
}

export const AuthAPI = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post('api/token/', { email, password });
    return response.data;
  },

  register: async (credentials: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    dob?: string;
    address?: string;
    password: string;
    confirm_password: string;
    user_role: string;
  }): Promise<{ message: string }> => {
    const response = await api.post('/signup/', credentials);
    return response.data;
  },

  verifyEmailCode: async (
    email: string,
    code: string
  ): Promise<{
    message: string;
    access_token: string;
    refresh_token: string;
    user: UserData;
  }> => {
    const response = await api.post('api/email/verify-code/', { email, code });
    return response.data;
  },

  resendVerificationCode: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('api/email/send-code/', { email });
    return response.data;
  },

  setupDoctor: async (setupData: {
    field_of_practice: string;
    business_name: string;
    practice_management_software: string;
    location: string;
    is_practice_manager: boolean;
  }): Promise<{
    message: string;
    doctor: {
      id: number;
      field_of_practice: string;
      is_verified: boolean;
      active_clinic_id: number;
    };
    clinic: {
      id: number;
      name: string;
      address: string;
      practice_management_software: string;
      owner_id: number;
    };
    is_practice_manager: boolean;
  }> => {
    const response = await api.post('/api/doctor/setup/', setupData);
    return response.data;
  },

  googleLogin: async (credential: string): Promise<LoginResponse> => {
    const response = await api.post('/accounts/google/login/', {
      credential,
      redirect_uri: window.location.origin,
    });
    return response.data;
  },

  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await api.post('/api/token/refresh/', { refresh: refreshToken });
    return response.data;
  },

  activateUser: async (payload: ActivateUserPayload): Promise<void> => {
    await api.get(`/activate/${payload.uid}/${payload.token}/`);
  },

  changePassword: async (payload: ChangePasswordPayload): Promise<ChangePasswordResponse> => {
    const response = await api.post('api/change-password/', payload);
    return response.data;
  },

  requestPasswordReset: async (email: string): Promise<PasswordResetResponse> => {
    const response = await api.post('api/password/reset/', { email });
    return response.data;
  },

  validateResetToken: async (token: string): Promise<PasswordResetResponse> => {
    const response = await api.get(`/reset/${token}/`);
    return response.data;
  },

  resetPasswordWithToken: async (
    token: string,
    payload: { password: string; confirm_password: string }
  ): Promise<PasswordResetResponse> => {
    const response = await api.post(`/reset/${token}/`, payload);
    return response.data;
  },

  updateProfile: async (payload: ProfileUpdatePayload): Promise<ProfileUpdateResponse> => {
    const response = await api.put('api/profile/update/', payload);
    return response.data;
  },

  updateClinic: async (payload: ClinicUpdatePayload): Promise<ClinicUpdateResponse> => {
    const response = await api.put('api/clinic/update/', payload);
    return response.data;
  },

  updateProfileAndClinic: async (
    payload: ProfileClinicUpdatePayload
  ): Promise<ProfileClinicUpdateResponse> => {
    const response = await api.put('api/profile-clinic/update/', payload);
    return response.data;
  },

  getCurrentUser: async (): Promise<UserData> => {
    const response = await api.get('current-user/');
    return response.data;
  },
};
